/**
 * Custom react-big-calendar `dayLayoutAlgorithm` (prototype `lanes()` +
 * `deco` positioning). Overlapping events go into lanes side by side, at
 * most `cap` wide (Day: no cap). Past the cap, the first `cap - 1` lanes show
 * and every overflowing run collapses into one "+N" chip in the last lane.
 *
 * RBC renders each styled event right after this runs (same render), so the
 * "+N" chip and the hidden events are handed to the event component / prop
 * getter through `laneOverflow(event)` (a WeakMap keyed by the event).
 */

interface RbcAccessors {
  start: (event: object) => Date;
  end: (event: object) => Date;
}

interface RbcSlotMetrics {
  getRange: (
    start: Date,
    end: Date,
  ) => { top: number; height: number; start: number; end: number };
}

interface RbcLayoutArgs {
  events: object[];
  slotMetrics: RbcSlotMetrics;
  accessors: RbcAccessors;
}

export interface StyledEvent {
  event: object;
  style: {
    top: number | string;
    height: number | string;
    width: number | string;
    xOffset: number | string;
  };
}

export type LaneOverflow =
  | { kind: "hidden" }
  | { kind: "more"; count: number; title: string };

const overflowByEvent = new WeakMap<object, LaneOverflow>();

const laneByEvent = new WeakMap<object, { lane: number; laneCount: number }>();

/** The shown event's lane and its group's (capped) lane count. */
export const laneMeta = (
  event: object,
): { lane: number; laneCount: number } | undefined => laneByEvent.get(event);

/** The event's "+N" role from the latest layout, if it overflowed. */
export const laneOverflow = (event: object): LaneOverflow | undefined =>
  overflowByEvent.get(event);

interface Span {
  start: number;
  end: number;
}

/**
 * Prototype `lanes()`: groups of transitively overlapping items; within a
 * group each item takes the first lane that is free at its start. Returns
 * lane + laneCount (the group's lane total) per item, in input order.
 */
export function assignLanes<T extends Span>(
  items: T[],
): { item: T; lane: number; laneCount: number }[] {
  const sorted = [...items].sort(
    (a, b) => a.start - b.start || b.end - b.start - (a.end - a.start),
  );
  const out = new Map<T, { lane: number; laneCount: number }>();
  let group: { item: T; lane: number }[] = [];
  let ends: number[] = [];
  let groupEnd = -Infinity;
  const flush = () => {
    group.forEach(({ item, lane }) =>
      out.set(item, { lane, laneCount: ends.length }),
    );
    group = [];
    ends = [];
    groupEnd = -Infinity;
  };
  sorted.forEach((item) => {
    if (group.length && item.start >= groupEnd) flush();
    let lane = ends.findIndex((e) => e <= item.start);
    if (lane < 0) lane = ends.length;
    ends[lane] = item.end;
    group.push({ item, lane });
    groupEnd = Math.max(groupEnd, item.end);
  });
  flush();
  return items.map((item) => ({ item, ...out.get(item)! }));
}

/**
 * Events are 3px shorter than their slot span, so a 15-min service reads as
 * 15 minutes at any zoom. Short blocks grow to 12px for a clickable sliver,
 * but never past their own span (1px gap), so at 50% a 15-min block stays
 * inside its 15-min slot.
 */
const heightOf = (pct: number) =>
  `max(calc(${pct}% - 3px), min(12px, calc(${pct}% - 1px)))`;

interface LaneLayoutOptions {
  /** Column inset from each side of the day column (px). */
  gap: number;
  /** Lanes shown before the "+N" chip. */
  cap?: number;
}

export function laneLayout({ gap, cap = Infinity }: LaneLayoutOptions) {
  const inner = `(100% - ${gap * 2}px)`;
  const column = (lane: number, n: number) =>
    n <= 1
      ? { xOffset: `${gap}px`, width: `calc(100% - ${gap * 2}px)` }
      : {
          xOffset: `calc(${gap}px + ${inner} * ${lane / n})`,
          width: `calc(${inner} / ${n} - 2px)`,
        };

  return ({ events, slotMetrics, accessors }: RbcLayoutArgs): StyledEvent[] => {
    const items = events.map((event) => {
      const range = slotMetrics.getRange(
        accessors.start(event),
        accessors.end(event),
      );
      return { event, range, start: range.start, end: range.end };
    });
    const laned = assignLanes(items);

    const shown: StyledEvent[] = [];
    const spill: typeof items = [];
    laned.forEach(({ item, lane, laneCount }) => {
      if (laneCount > cap && lane >= cap - 1) {
        spill.push(item);
        return;
      }
      overflowByEvent.delete(item.event);
      const n = Math.min(laneCount, cap);
      laneByEvent.set(item.event, { lane, laneCount: n });
      shown.push({
        event: item.event,
        style: {
          top: item.range.top,
          height: heightOf(item.range.height),
          ...column(lane, n),
        },
      });
    });

    // Overflow: consecutive overlapping spill events form one "+N" chip.
    const runs: (typeof items)[] = [];
    [...spill]
      .sort((a, b) => a.start - b.start)
      .forEach((item) => {
        const last = runs[runs.length - 1];
        if (last && item.start < Math.max(...last.map((x) => x.end))) {
          last.push(item);
        } else {
          runs.push([item]);
        }
      });
    const chips: StyledEvent[] = [];
    runs.forEach((run) => {
      const top = Math.min(...run.map((x) => x.range.top));
      const bottom = Math.max(...run.map((x) => x.range.top + x.range.height));
      const style = {
        top,
        height: `max(28px, calc(${bottom - top}% - 3px))`,
        xOffset: `calc(${gap}px + ${inner} * ${(cap - 1) / cap})`,
        width: `calc(${inner} / ${cap} - 2px)`,
      };
      const title = `${run.length} more · ${run
        .map((x) => (x.event as { title?: string }).title || "")
        .filter(Boolean)
        .join(", ")}`;
      run.forEach((x, i) => {
        laneByEvent.delete(x.event);
        overflowByEvent.set(
          x.event,
          i === 0
            ? { kind: "more", count: run.length, title }
            : { kind: "hidden" },
        );
        chips.push({ event: x.event, style });
      });
    });

    return [...shown, ...chips].map(({ event, style }) => ({ event, style }));
  };
}
